import type { GradeGroup, WorksheetSubject } from "@arbeidsark/shared"
import { captureEvent } from "~/modules/analytics/capture"

export type WorksheetConfigType =
  | "difficulty"
  | "grade"
  | "language_theme"
  | "operation"
  | "pages"
  | "size"
  | "subject"
  | "theme"

export interface WorksheetAnalyticsProperties {
  subject?: WorksheetSubject
  grade?: number | null
  gradeGroup?: GradeGroup | null
  source?: string
  worksheet_id?: number
}

interface WorksheetConfigChangeProperties extends WorksheetAnalyticsProperties {
  subject: WorksheetSubject
}

function normalizeAnalyticsValue(
  value: unknown,
): string | number | boolean | null {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value === "string") {
    return value
  }

  if (typeof value === "number") {
    return value
  }

  if (typeof value === "boolean") {
    return value
  }

  if (Array.isArray(value)) {
    return value.map((entry) => normalizeAnalyticsValue(entry)).join(",")
  }

  return JSON.stringify(value)
}

function areValuesEqual(oldValue: unknown, newValue: unknown) {
  if (oldValue === newValue) {
    return true
  }

  return JSON.stringify(oldValue) === JSON.stringify(newValue)
}

export function captureWorksheetDownload(
  properties: WorksheetAnalyticsProperties = {},
) {
  captureEvent("download_worksheet", properties)
}

export function captureWorksheetSaved(
  properties: WorksheetAnalyticsProperties = {},
) {
  captureEvent("save_worksheet", properties)
}

export function captureWorksheetConfigChange(
  properties: WorksheetConfigChangeProperties,
  configType: WorksheetConfigType,
  oldValue: unknown,
  newValue: unknown,
) {
  if (areValuesEqual(oldValue, newValue)) {
    return
  }

  captureEvent("change_worksheet_config", {
    ...properties,
    config_type: configType,
    old_value: normalizeAnalyticsValue(oldValue),
    new_value: normalizeAnalyticsValue(newValue),
  })
}
